/********************************************************************
 * Auto complete module
 *
 * Only show cities. If we also want to show courses we need
 * the get the bookable status before showing them in autocomplete.
 * Or find a way to make noticed that they do not have any
 * availability.
 *
 *
 * AfterXCharacters =>	- load 'auto complete courses'
 *						- show 'auto complete courses'
 *
 *	OnClick =>  - Select the option
 *				-  Fill CityId in FilterContext / Local Storage
 *
 ********************************************************************/
"use strict";
import { isElement, debounce, forEach } from "lodash";
import { get as getPageOptions } from "../../01-helpers/utilities/options";
import containerManager from "../../01-helpers/filter-context/container-manager";

const moduleName = "autocomplete",
  selectors = {
    base: `.js-${moduleName}`,
    input: `.js-${moduleName}-input`,
    list: `.js-${moduleName}-list`,
    listItem: `.js-${moduleName}-list-item`,
  },
  optionNames = {
    nodeId: "NodeId",
    page: "Page",
  };

export default class AutoComplete {
  constructor(baseElement) {
    if (!isElement(baseElement)) {
      console.error(`Element has not been found.`);
      return false;
    }

    this._config(baseElement);
    this._setup();
  }

  /**********************************************************************/
  closeContainer() {
    this.list.innerHTML = "";
  }
  /**********************************************************************/

  /**
   * Blur event handler
   *
   * @param {any} evt
   */
  _onBlurHandler(evt) {
    this.closeContainer();
  }

  /**
   * Handler for Click event on ListItem
   *
   * @param {any} evt - Click (Mouse) event
   */
  _onItemClickHandler(evt) {
    const element = evt.currentTarget || evt.target,
      id = element.dataset.id,
      value = element.dataset.value,
      filterContextField = this.baseElement.getAttribute("data-selection-type"),
      filterContextId = element.dataset?.attr
        ? JSON.parse(element.dataset.attr)["data-selection-id"]
        : this.baseElement.getAttribute("data-selection-id");

    // set the value of chosen item in the input as searchTerm
    this.trigger.value = value;

    this.filterContext.update({
      [filterContextField]: value,
      [filterContextId]: id,
    });

    this.input.dataset.autofill = true;
    // close the container after a selection is made
    this.closeContainer();
  }

  /**
   * Submit event handler - handles both regular autocomplete and places autocomplete
   *
   * @param {any} evt - Submit event
   */
  _onSubmitHandler(evt) {
    // Prevent default form submission altijd
    evt.preventDefault();

    // if input is not autofilled from autocomplete, we block the submit
    if (this.input.dataset.autofill == "false") {
      if (this.list.childElementCount > 0) {
        this.input.dataset.autofill = true;
        this.list.children[0].querySelector("button").click();
        // Na de click wordt deze handler opnieuw aangeroepen, maar dan met autofill = true
        return;
      }
      // Geen autocomplete resultaten, niet submitten
      return;
    }

    // Als we hier komen, is autofill = true en hebben we een geldige selectie
    // Check of dit de places autocomplete is (voor location-based search)
    if (this.baseElement.classList.contains("js-autocomplete-places")) {
      this._handlePlacesSubmit();
    } else {
      // Voor andere types autocomplete, doe gewone form submission
      this._doNormalFormSubmit();
    }
  }

  /**
   * Handle places autocomplete submission with base64 encoding
   */
  _handlePlacesSubmit() {
    // Haal de form action op (set by Umbraco per site)
    const action = this.baseElement.getAttribute("action") || "/trainingen/";

    // Check of we al op de target pagina zijn (dan geen redirect nodig)
    const currentPath = window.location.pathname;
    if (currentPath.includes(action)) {
      // We zijn al op de pagina, gebruik bestaande filterContext logic
      this._doNormalFormSubmit();
      return;
    }

    // Zoek de hidden input velden voor coordinates
    const neInput = this.baseElement.querySelector(
      ".js-autocomplete-northeast",
    );
    const swInput = this.baseElement.querySelector(
      ".js-autocomplete-southwest",
    );

    if (neInput && swInput && neInput.value && swInput.value) {
      // Parse de coordinates
      const ne = neInput.value.split(",");
      const sw = swInput.value.split(",");

      // Haal de geselecteerde radius op (standaard 2000 = 2KM)
      const radiusDropdown = this.baseElement.querySelector(
        "select.c-search-box__dd",
      );
      const searchRadius = radiusDropdown
        ? parseInt(radiusDropdown.value)
        : 2000;

      // Bouw het filter object voor base64 encoding
      const filterObject = {
        f: [], // filters
        p: 1, // page
        q: this.input.value, // search query
        sr: searchRadius, // search radius
        bb: [
          parseFloat(ne[0]), // northeast lat
          parseFloat(ne[1]), // northeast lng
          parseFloat(sw[0]), // southwest lat
          parseFloat(sw[1]), // southwest lng
        ],
        c: [
          // center point berekenen
          (parseFloat(ne[0]) + parseFloat(sw[0])) / 2,
          (parseFloat(ne[1]) + parseFloat(sw[1])) / 2,
        ],
      };

      // Base64 encoderen
      const base64Data = btoa(JSON.stringify(filterObject));

      // DIRECT redirect zonder filterContext.update
      window.location.href = `${action}#${base64Data}`;
    } else {
      // Fallback naar normale form submission als coordinates niet beschikbaar zijn
      this._doNormalFormSubmit();
    }
  }

  /**
   * Perform normal form submission (fallback)
   */
  _doNormalFormSubmit() {
    // Remove event listener tijdelijk om infinite loop te voorkomen
    const boundHandler = this._onSubmitHandler.bind(this);
    this.baseElement.removeEventListener("submit", boundHandler);

    // Trigger normale form submission
    this.baseElement.submit();

    // Re-attach event listener voor volgende submissions
    setTimeout(() => {
      this.baseElement.addEventListener("submit", boundHandler);
    }, 100);
  }

  /**
   * KeyUp event handler
   * @param {any} evt
   */
  _onKeyUpHandler(evt) {
    this.input.dataset.autofill = false;
  }

  /**
   * Set click event handler on listItems in the List
   * @param {any} list - list that contains the listItems
   */
  _setClickEvent(list) {
    const listItems = list.querySelectorAll(selectors.listItem);

    forEach(listItems, (listItem) => {
      listItem.addEventListener("click", (event) => {
        this._onItemClickHandler(event);
      });
    });
  }

  /**
   * Bind event handling
   **/
  _bindEvents() {
    // onfocus event handler
    this.input.addEventListener(
      "blur",
      debounce(this._onBlurHandler.bind(this), 500, {
        leading: false,
        trailing: true,
      }),
      true,
    );

    this.baseElement.addEventListener(
      "submit",
      this._onSubmitHandler.bind(this),
    );
    // onkeydown event handler
    this.input.addEventListener(
      "keydown",
      debounce(this._onKeyUpHandler.bind(this), 300, {
        leading: false,
        trailing: true,
      }),
      true,
    );
  }

  _config(baseElement) {
    this.baseElement = baseElement;
    this.input = this.baseElement.querySelector(selectors.input);
    this.list = this.baseElement.querySelector(selectors.list);
    this.filterContext = containerManager.getContainer();
    this.NodeId = getPageOptions(optionNames.nodeId);
    this.Page = parseInt(getPageOptions(optionNames.page)) || 1;
  }

  _setup() {
    this._bindEvents();
  }
}
